import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import authFetch from '../../utils/authFetch';
import { motion } from 'framer-motion';
import { FiUser, FiLock, FiLogIn } from 'react-icons/fi';
import { jwtDecode } from 'jwt-decode'; // Import jwtDecode
import DarkModeToggle from '../../components/ui/DarkModeToggle'; // Import DarkModeToggle

const apiUrl = import.meta.env.VITE_API_URL;

const PeekingCharacter = ({ isFocused }) => (
  <motion.div
    className="absolute -top-12 right-0 left-0 mx-auto w-24 h-12"
    initial={{ y: 20, opacity: 0 }}
    animate={{
      y: isFocused ? 0 : 20,
      opacity: isFocused ? 1 : 0,
    }}
    transition={{ duration: 0.3 }}
  >
    <div className="w-full h-full bg-gray-300 dark:bg-gray-700 rounded-t-full border-4 border-gray-400 dark:border-gray-800 overflow-hidden relative">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/4 w-12 h-6 flex justify-around items-center">
        <div className="w-3 h-3 bg-gray-900 dark:bg-white rounded-full"></div>
        <div className="w-3 h-3 bg-gray-900 dark:bg-white rounded-full"></div>
      </div>
    </div>
  </motion.div>
);

const LoginV1 = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        const userRole = decodedToken.role;
        if (userRole === 'kasir') {
          navigate('/cashier-dashboard');
        } else if (userRole === 'owner') {
          navigate('/owner-dashboard');
        } else if (userRole === 'pelanggan') {
          navigate('/customer/dashboard');
        } else if (userRole === 'penjual') {
          navigate('/seller/dashboard');
        } else {
          navigate('/main-dashboard');
        }
      } catch (error) {
        console.error("Error decoding token in useEffect:", error);
        localStorage.removeItem('token');
        navigate('/login');
      }
    }
  }, [navigate]);
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await authFetch(`${apiUrl}/api/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('token', data.token);
        const decodedToken = jwtDecode(data.token);
        localStorage.setItem('userId', decodedToken.userId);
        localStorage.setItem('role', decodedToken.role);
        localStorage.setItem('username', decodedToken.username || formData.username);

        // Jadikan tema admin sebagai default UI di awal untuk user baru login
        const lastUserId = localStorage.getItem('last_logged_in_user_id');
        if (!lastUserId || lastUserId !== String(decodedToken.userId) || !localStorage.getItem('theme_user_selected')) {
          localStorage.setItem('app-theme', 'admin');
          localStorage.removeItem('theme_user_selected');
          window.dispatchEvent(new CustomEvent('app-theme-reset', { detail: 'admin' }));
        }
        localStorage.setItem('last_logged_in_user_id', String(decodedToken.userId));

        const userRole = decodedToken.role;
        if (userRole === 'kasir') {
          navigate('/cashier-dashboard');
        } else if (userRole === 'owner') {
          navigate('/owner-dashboard');
        } else if (userRole === 'pelanggan') {
          navigate('/customer/dashboard');
        } else if (userRole === 'penjual') {
          navigate('/seller/dashboard');
        } else {
          navigate('/main-dashboard');
        }
      } else {
        setError(data.error || 'Login failed');
      }
    } catch (err) {
      setError('Network error or server unreachable');
      console.error('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gradient-to-br dark:from-gray-900 dark:to-gray-800 p-4 overflow-hidden">
      <div className="absolute top-4 right-4 z-20 block md:hidden">
        <DarkModeToggle />
      </div>
      <motion.div
        initial={{ opacity: 0, y: -50, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="relative w-full max-w-md bg-white/50 dark:bg-gray-800/50 backdrop-blur-lg rounded-2xl shadow-2xl p-8 z-10"
      >
        <PeekingCharacter isFocused={isPasswordFocused} />
        <div className="absolute top-0 left-0 -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-purple-500 rounded-full filter blur-3xl opacity-30 animate-blob"></div>
        <div className="absolute bottom-0 right-0 translate-x-1/2 translate-y-1/2 w-32 h-32 bg-blue-500 rounded-full filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>

        <div className="text-center mb-8">
          <motion.h2
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white tracking-wider"
          >
            Selamat Datang Kembali
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="text-gray-600 dark:text-gray-400 text-sm mt-2"
          >
            Masuk untuk melanjutkan ke dasbor Anda
          </motion.p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="relative"
          >
            <FiUser className="absolute top-1/2 left-4 -translate-y-1/2 text-gray-400" />
            <input
              name="username"
              type="text"
              placeholder="Nama Pengguna"
              value={formData.username}
              onChange={handleInputChange}
              required
              className="w-full bg-gray-200/50 dark:bg-gray-900/50 text-gray-900 dark:text-white placeholder-gray-500 rounded-lg py-3 pl-12 pr-4 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-300"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.8 }}
            className="relative"
          >
            <FiLock className="absolute top-1/2 left-4 -translate-y-1/2 text-gray-400" />
            <input
              name="password"
              type="password"
              placeholder="Kata Sandi"
              value={formData.password}
              onChange={handleInputChange}
              onFocus={() => setIsPasswordFocused(true)}
              onBlur={() => setIsPasswordFocused(false)}
              required
              className="w-full bg-gray-200/50 dark:bg-gray-900/50 text-gray-900 dark:text-white placeholder-gray-500 rounded-lg py-3 pl-12 pr-4 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300"
            />
          </motion.div>

          {error && (
            <motion.p
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="text-red-600 dark:text-red-400 text-sm text-center"
            >
              {error}
            </motion.p>
          )}

          <div className="text-right -mt-2">
            <Link to="/forgot-password" className="text-sm text-purple-600 dark:text-purple-400 hover:underline">
              Lupa kata sandi?
            </Link>
          </div>

          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 1 }}
            type="submit"
            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-bold py-3 px-4 rounded-lg shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center"
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="w-6 h-6 border-4 border-t-transparent border-white rounded-full animate-spin"></div>
            ) : (
              <>
                <FiLogIn className="mr-2" />
                Masuk
              </>
            )}
          </motion.button>
        </form>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 1.2 }}
          className="text-center text-sm text-gray-600 dark:text-gray-400 mt-8"
        >
          Belum punya akun?{' '}
          <Link to="/register" className="text-purple-600 dark:text-purple-400 hover:underline">
            Daftar di sini
          </Link>
        </motion.p>
      </motion.div>
    </div>
  );
};

export default LoginV1;
